import React, { useEffect, useRef, useState } from "react";
import coup from "./assets/sa.jpg";
import checkIcon from "./assets/check-blue.svg";
import "./Demo.css";

const SoftwareDemo = () => {

  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = screenWidth <= 768;
  const isTablet = screenWidth > 768 && screenWidth <= 1024;

  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [userCaptcha, setUserCaptcha] = useState("");

  const [captchaCode, setCaptchaCode] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [token, setToken] = useState("");

  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [emailError, setEmailError] = useState("");

  const [countryCode, setCountryCode] = useState("+91");

  const mobileRules = {
    "+91": 10, "+1": 10, "+44": 10, "+61": 9, "+65": 8,
    "+971": 9, "+60": 9, "+81": 10, "+49": 11, "+33": 9,
    "+94": 9, "+62": 11, "+66": 9, "+82": 10, "+86": 11,
    "+55": 11, "+7": 10, "+27": 9, "+39": 10, "+34": 9,
    "+31": 9, "+41": 9, "+46": 10, "+47": 8, "+52": 10,
    "+234": 10, "+63": 10, "+20": 10, "+90": 10,
  };

  const countryOptions = [
    { iso: "IN", code: "+91" }, { iso: "US", code: "+1" },
    { iso: "GB", code: "+44" }, { iso: "AU", code: "+61" },
    { iso: "SG", code: "+65" }, { iso: "AE", code: "+971" },
    { iso: "MY", code: "+60" }, { iso: "JP", code: "+81" },
    { iso: "DE", code: "+49" }, { iso: "FR", code: "+33" },
  ];

  const canvasRef = useRef(null);
  const inputStyle = "demo-input";

  const handleNameChange = (e) => {
    const v = e.target.value;
    if (/^[A-Za-z\s]*$/.test(v) && v.length <= 30) setName(v);
  };

  const handleMobileChange = (e) => {
    const v = e.target.value.replace(/\D/g, "");
    if (v.length <= (mobileRules[countryCode] || 15)) setContactNumber(v);
  };

  const validateEmail = (v) => {
    const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(r.test(v) ? "" : "Please enter a valid email address");
  };

  const validateMobile = (v) => {
    if (!v) return true;
    if (!/^\d+$/.test(v) || v.length < 7 || v.length > 15) {
      setFormError("Please enter a valid mobile number.");
      return false;
    }
    return true;
  };

  const fetchCaptcha = async () => {
  try {
    const response = await fetch("https://api.app.mawalog.com/v1.2/website", {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Captcha API failed: ${response.status}`);
    }

    const json = await response.json();

    if (json?.data) {
      setCaptchaCode(String(json.data.captcha_code || ""));
      setSessionId(String(json.data.session_id || ""));
      setToken(String(json.data.token || ""));
    } else {
      console.warn("Unexpected captcha response:", json);
    }
  } catch (err) {
    console.error("Error fetching captcha:", err);
    setCaptchaCode("");
  }
};


  useEffect(() => {
    fetchCaptcha();
  }, []);

  useEffect(() => {
    if (!captchaCode || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.strokeStyle = `rgba(0,0,0,${Math.random() * 0.25})`;
      ctx.stroke();
    }

    captchaCode.split("").forEach((ch, i) => {
      ctx.save();
      ctx.translate(12 + i * 20, 26 + Math.random() * 6);
      ctx.rotate((Math.random() - 0.5) * 0.5);
      ctx.font = "bold 20px Arial";
      ctx.fillStyle = "#004AAD";
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    });
  }, [captchaCode]);

  const handleSubmit = async () => {
    setFormError("");
    setFormSuccess("");

    if (!name || !email || !message || !userCaptcha) {
      setFormError("Please fill in Name, Email, Message and Captcha.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    if (contactNumber && !validateMobile(contactNumber)) return;

    if (userCaptcha.trim() !== captchaCode.trim()) {
      setFormError("Invalid captcha. Please try again.");
      fetchCaptcha();
      return;
    }

    try {
      const res = await fetch("https://api.app.mawalog.com/v1.2/website", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": token,
        },
        body: JSON.stringify({
          session_id: sessionId,
          captcha: userCaptcha.trim(),
          name: name.trim(),
          email: email.trim(),
          contact_number: contactNumber ? `${countryCode} ${contactNumber}` : "",
          message: message.trim(),
          type: "Mawalog",
        }),
      });

      const result = await res.json();

      if (result?.success) {
        setFormSuccess("Enquiry created successfully!");
        setName("");
        setEmail("");
        setContactNumber("");
        setMessage("");
        setUserCaptcha("");
        fetchCaptcha();
      } else {
        setFormError(result?.message || "Submission failed.");
        fetchCaptcha();
      }
    } catch {
      setFormError("Something went wrong. Please try again.");
      fetchCaptcha();
    }
  };

  return (
    <>
      <div className={`demo-hero ${isMobile ? "mobile" : isTablet ? "tablet" : "desktop"}`}>
        <div className={`demo-hero-title ${isMobile ? "mobile" : isTablet ? "tablet" : "desktop"}`}>
          JIVIEWS Software Demo
        </div>
      </div>

      <div className={`demo-bg ${isMobile ? "mobile" : isTablet ? "tablet" : "desktop"}`}>
        <div className={`demo-container ${isMobile ? "mobile" : ""}`}>
          <div>
            <div className={`demo-left-title ${isMobile ? "mobile" : isTablet ? "tablet" : ""}`}>
              What's included in the demo?
            </div>

            <div className={`demo-list ${isMobile ? "mobile" : ""}`}>
              {[
                "A live walkthrough of our product features",
                "Minimum training, maximum adoption",
                "Automated roster creation with minimal user interaction",
                "Track your Shift and Leave tasks on the go",
              ].map((t) => (
                <div key={t} className="demo-list-item">
                  <img src={checkIcon} alt="" />
                  <span>{t}</span>
                </div>
              ))}
            </div>

            {coup && (
              <div className={`demo-image-wrap ${isMobile || isTablet ? "center" : "left"}`}>
                <img src={coup} alt="" className={`demo-image ${isMobile ? "mobile" : isTablet ? "tablet" : "desktop"}`} />
              </div>
            )}
          </div>

          <div className={`demo-form-wrapper ${isMobile ? "mobile" : isTablet ? "tablet" : "desktop"}`}>
            <div className={`demo-form-card ${isMobile ? "mobile" : ""}`}>
              <h2>Book a free 60-minute product demo</h2>

              <input className={inputStyle} placeholder="Name" value={name} onChange={handleNameChange} />
              <input
                className={inputStyle}
                placeholder="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  validateEmail(e.target.value);
                }}
              />

              {emailError && <p className="demo-error">{emailError}</p>}

              <div className="demo-mobile-row">
                <select className="demo-select" value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                  {countryOptions.map((c) => (
                    <option key={c.iso} value={c.code}>
                      {c.code} ({c.iso})
                    </option>
                  ))}
                </select>

                <input
                  className={`${inputStyle} demo-mobile-input`}
                  placeholder="Mobile Number (optional)"
                  value={contactNumber}
                  onChange={handleMobileChange}
                />
              </div>

              <textarea
                className={`${inputStyle} demo-textarea`}
                placeholder="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div className="demo-captcha">
                <canvas ref={canvasRef} width={130} height={40} />
                <button type="button" className="demo-refresh" onClick={() => { fetchCaptcha(); setUserCaptcha(""); }}>
                  ⟳
                </button>
                <input
                  className="demo-captcha-input"
                  placeholder="Enter captcha"
                  value={userCaptcha}
                  onChange={(e) => /^\d{0,3}$/.test(e.target.value) && setUserCaptcha(e.target.value)}
                />
              </div>

              <button className="demo-submit" onClick={handleSubmit}>Submit</button>

              {formError && <p className="demo-error">{formError}</p>}
              {formSuccess && <p className="demo-success">{formSuccess}</p>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SoftwareDemo;
